import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import IconCheckmarkCircleFill from '@/assets/icons/icon-checkmark-circle-fill.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { publishPage, fetchPublishedLandingPageCount } from '@/store/pages/thunks';
import { useParams } from 'react-router-dom';
import {
  selectCurrentPage,
  selectPublishStatus,
  selectPublishedUrl,
} from '@/store/pages/selectors';
import { resetPublishing } from '@/store/pages/slice';
import IconCopy from '@/assets/icons/icon-copy.svg?react';
import IconLinkSquare from '@/assets/icons/icon-link-square.svg?react';
import { selectDomains, selectSelectedDomain } from '@/store/domains/selectors';
import { setSelectedDomain } from '@/store/domains/slice';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, PROPERTY } from '@/analytics/mixpanel';
import { selectIsPublishingBlocked, selectUser } from '@/store/user/selectors';
import { showToast } from '@/store/notifications/slice';
import { UpgradeToPublishModal } from '@/components/dialogs/UpgradeToPublishModal/UpgradeToPublishModal';

interface NavbarPublishProps {
  className: string;
  onClick?: () => void;
  text?: string;
  onClose?: () => void;
}

export const NavbarPublish: React.FC<NavbarPublishProps> = ({
  className = '',
  onClick,
  text = 'Publish',
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const domainList = useAppSelector(selectDomains);
  const selectedDomain = useAppSelector(selectSelectedDomain);
  const currentPage = useAppSelector(selectCurrentPage);
  const publishingStatus = useAppSelector(selectPublishStatus);
  const isPublishingBlocked = useAppSelector(selectIsPublishingBlocked);
  const publishedUrl = useAppSelector(selectPublishedUrl);
  const user = useAppSelector(selectUser);
  const { uuid } = useParams<{ uuid: string }>();
  const track = useMixpanel();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isPublishInFlight = useRef(false);

  // WHY: Closure over user, currentPage, and dispatch from component scope
  const isAtPageLimit = async (): Promise<boolean | null> => {
    const limit = user?.featureAccess?.leadpageLimit;
    if (limit === undefined || limit === null) return false;

    // If this page is already published, it's an update — not a new publish
    if (currentPage?.publishedVersionId) return false;

    try {
      const { total } = await dispatch(fetchPublishedLandingPageCount()).unwrap();
      return total >= limit;
    } catch {
      dispatch(
        showToast({
          text: 'Unable to verify your published page limit. Please try again.',
          state: 'error',
        }),
      );
      return null;
    }
  };

  const handlePublish = async () => {
    if (isPublishingBlocked) {
      const mainAppUrl = import.meta.env.VITE_API_MAIN_APP;
      dispatch(
        showToast({
          text: 'Ready to publish? End your trial now to take your page live.',
          state: 'info',
          buttonPrimaryText: 'End Trial',
          buttonPrimaryHref: `${mainAppUrl}/my-account/billing?endTrialModal=1`,
        }),
      );
      return;
    }
    if (publishingStatus !== 'idle') return;
    if (!uuid) return;
    if (isPublishInFlight.current) return;

    isPublishInFlight.current = true;
    try {
      await handlePublishFlow(uuid);
    } finally {
      isPublishInFlight.current = false;
    }
  };

  // WHY: Closure over component state (user, currentPage, dispatch, track) is required
  const handlePublishFlow = async (pageId: string) => {
    const atPageLimit = await isAtPageLimit();
    if (atPageLimit === null) return;
    if (atPageLimit) {
      setShowUpgradeModal(true);
      return;
    }

    const publishTarget = currentPage?.publishedVersionId ? 'update_page' : 'new_page';

    track(EVENT.PUBLISH_CLICKED, {
      [PROPERTY.PUBLISH_TARGET]: publishTarget,
    });

    const startTime = Date.now();

    try {
      await dispatch(publishPage({ pageId })).unwrap();

      const durationMs = Date.now() - startTime;
      const durationSeconds = Math.round(durationMs / 1000);

      track(EVENT.PUBLISH_COMPLETED, {
        [PROPERTY.LOAD_TIME]: durationSeconds,
      });
    } catch (error) {
      let errorType = 'other';
      const errorMessage = typeof error === 'string' ? error : '';

      if (errorMessage.includes('No Page Name') || errorMessage.includes('validation')) {
        errorType = 'validation';
      } else if (errorMessage.includes('domain')) {
        errorType = 'domain';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        errorType = 'network';
      }

      track(EVENT.PUBLISH_FAILED, {
        [PROPERTY.ERROR_TYPE]: errorType,
      });
    }

    onClick?.();
  };

  const handleCopyUrl = () => {
    if (publishedUrl) {
      navigator.clipboard.writeText(publishedUrl);
    }
  };

  const handleOpenInNewTab = () => {
    if (publishedUrl) {
      window.open(publishedUrl, '_blank');
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetPublishing());
    };
  }, [dispatch]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex w-[256px] flex-col items-stretch gap-3 rounded-lg border border-neutral-100 pb-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]',
        className,
      )}
    >
      {/* Header */}
      <div className="bg-neutral-0 inline-flex h-10 items-center justify-between rounded-t-lg border-b border-neutral-100 pr-2 pl-3">
        <div className="flex flex-1 items-center justify-between py-1">
          <span className="text-xs leading-none font-medium text-neutral-900">Publish</span>
          <IconSimpleButton
            icon={IconCancel}
            size={24}
            state="default"
            onClick={onClose}
            data-qa-selector="navbar-publish-close-button"
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col items-start gap-3 self-stretch px-3 pb-1">
        <span className="text-xs leading-none font-medium text-neutral-500">URL</span>

        {/* URL select field */}
        <div className="inline-flex h-8 w-full items-center justify-between">
          <div className="flex min-w-0 flex-1 gap-1">
            <SelectField
              text=""
              size="none"
              className="w-full min-w-0"
              classNameTrigger="w-full min-w-0"
              defaultValue={selectedDomain || currentPage?.domain || ''}
              placeholder="Select a domain"
              onValueChange={(value) => dispatch(setSelectedDomain(value))}
              data-qa-selector="navbar-publish-domain-select"
            >
              {domainList.map((d) => (
                <SelectItem value={d.domain} key={d.domain}>
                  {d.domain}
                </SelectItem>
              ))}
            </SelectField>
            <IconSimpleButton
              icon={IconCopy}
              tooltipContent="Copy link"
              size={32}
              state={currentPage?.publishedVersionId ? 'default' : 'disabled'}
              onClick={handleCopyUrl}
              data-qa-selector="navbar-publish-copy-url-button"
            />
            <IconSimpleButton
              icon={IconLinkSquare}
              tooltipContent="Open in new tab"
              size={32}
              state={currentPage?.publishedVersionId ? 'default' : 'disabled'}
              onClick={handleOpenInNewTab}
              data-qa-selector="navbar-publish-open-tab-button"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex w-full flex-col items-start gap-1">
          <div className="inline-flex w-full items-center gap-1">
            {publishingStatus === 'idle' && (
              <PrimaryButton
                icon={null}
                size="sm"
                state="default"
                text={text}
                className="h-7 w-full"
                onClick={handlePublish}
                data-qa-selector="navbar-publish-submit-button"
              />
            )}
            {publishingStatus === 'publishing' && (
              <PrimaryButton
                icon={IconSpinner}
                size="sm"
                state="loading"
                text="Publishing..."
                className="h-7 w-full"
                data-qa-selector="navbar-publish-submit-button"
              />
            )}
            {publishingStatus === 'published' && (
              <PrimaryButton
                icon={IconCheckmarkCircleFill}
                size="sm"
                state="default"
                disabled={true}
                text="Published"
                className="h-7 w-full"
                data-qa-selector="navbar-publish-submit-button"
              />
            )}
          </div>
        </div>
      </div>
      <UpgradeToPublishModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
};
